"use client";

import { useSearchParams } from "next/navigation";
import Script from "next/script";
import React, { useState } from "react";

interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare let grecaptcha: Grecaptcha;

export interface EbookDownloadSectionProps {
  title?: string;
  subtitle?: string;
  backgroundImageUrl?: string;
  buttonLabel?: string;
}

export default function EbookDownloadSection({
  title = "Download the Hair Loss Guide",
  subtitle,
  backgroundImageUrl,
  buttonLabel = "Download the Hair Loss Guide",
}: EbookDownloadSectionProps) {
  const searchParams = useSearchParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";

  // Get UTM parameters from URL
  const getUTMParams = () => ({
    utm_source: searchParams.get("utm_source") || undefined,
    utm_medium: searchParams.get("utm_medium") || undefined,
    utm_campaign: searchParams.get("utm_campaign") || undefined,
    utm_term: searchParams.get("utm_term") || undefined,
    utm_content: searchParams.get("utm_content") || undefined,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    if (typeof grecaptcha === "undefined") {
      console.error("reCAPTCHA client is not available.");
      setErrorMessage("reCAPTCHA client is not ready. Please try again later.");
      setLoading(false);
      return;
    }

    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(SITE_KEY, {
          action: "submit",
        });

        // Prepare form data with UTM parameters
        const submissionData = {
          ...formData,
          token,
          formType: "ebook",
          ...getUTMParams(),
        };

        const response = await fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(submissionData),
        });

        if (response.ok) {
          const responseData = await response.json();
          setSuccessMessage(responseData.message);
          setSubmitted(true);
          setErrorMessage("");

          // Redirect to thank you page after a short delay
          setTimeout(() => {
            window.location.href = "/thank-you-consultation";
          }, 1000);

          // GTM tracking
          if (typeof window !== "undefined" && window.dataLayer) {
            window.dataLayer.push({
              event: "trichos_form_submit",
              formType: "ebook",
              formId: "trichos-ebook-form",
              formName: "Ebook Download Form",
              submissionStatus: "success",
            });
          }
        } else {
          const errorData = await response.json();
          setErrorMessage(errorData.message || "Error submitting form.");
          setSuccessMessage("");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        setErrorMessage("An error occurred. Please try again.");
        setSuccessMessage("");
      } finally {
        setLoading(false);
      }
    });
  };

  const handleReset = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
    });
    setSubmitted(false);
    setSuccessMessage("");
    setErrorMessage("");
  };

  const backgroundStyle = backgroundImageUrl
    ? {
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : {};

  return (
    <section className="py-16 md:py-24">
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`}
        strategy="lazyOnload"
      />
      <div className="mx-auto w-full max-w-screen-2xl px-4 md:px-6">
        <div
          className="relative overflow-hidden rounded-3xl p-8 md:p-12 lg:p-16"
          style={backgroundStyle}
        >
          {/* Overlay for better text readability if background image exists */}
          {backgroundImageUrl && (
            <div className="absolute inset-0 bg-black/40" />
          )}

          {/* Content positioned at left top */}
          <div className="relative z-10 max-w-3xl">
            {submitted ? (
              <div className="text-white">
                {successMessage && (
                  <p className="mb-4 text-xl text-green-300">
                    {successMessage}
                  </p>
                )}
                {errorMessage && (
                  <p className="mb-4 text-xl text-red-300">{errorMessage}</p>
                )}
                <button
                  onClick={handleReset}
                  className="rounded-full bg-white px-6 py-3 font-semibold text-gray-900 transition-colors hover:bg-gray-100"
                >
                  Reset Form
                </button>
              </div>
            ) : (
              <>
                {/* Title - responsive white, compressed letters */}
                {title && (
                  <h2
                    className="mb-4 text-3xl font-bold leading-tight text-white md:text-4xl lg:text-5xl xl:text-6xl"
                    style={{
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {title}
                  </h2>
                )}

                {/* Subtitle */}
                {subtitle && (
                  <p className="mb-8 text-lg text-white/90">{subtitle}</p>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} id="trichos-ebook-form">
                  <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-white">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full rounded-lg border border-white/30 bg-white/10 px-4 py-2 text-white placeholder:text-white/60 backdrop-blur-sm focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-white">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full rounded-lg border border-white/30 bg-white/10 px-4 py-2 text-white placeholder:text-white/60 backdrop-blur-sm focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                        placeholder="Your email"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-white">
                        Phone
                      </label>
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full rounded-lg border border-white/30 bg-white/10 px-4 py-2 text-white placeholder:text-white/60 backdrop-blur-sm focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                        placeholder="Your phone"
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className={`mb-4 rounded-full bg-white px-8 py-3 text-base font-semibold text-gray-900 transition-colors ${
                      loading
                        ? "cursor-not-allowed opacity-50"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {loading ? "Submitting..." : buttonLabel}
                  </button>

                  {/* Disclaimer */}
                  <p className="text-xs text-white/80">
                    By downloading, you agree to our{" "}
                    <a
                      className="underline hover:text-white"
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>{" "}
                    &{" "}
                    <a
                      className="underline hover:text-white"
                      href="https://policies.google.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms
                    </a>
                    .
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
